import Image from "next/legacy/image"
import { useEffect, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useAppContext } from "@/lib/contexts/appContext"
import { AmountItem } from "@/components/checkout-form/amount/amount"
import CustomValue from "@/components/checkout-form/amount/customValue"
import Button from "@/common/button/button"
import CheckmarkCircle from "@/common/partials/checkmarkCircle"
import Block from "@/components/wrappers/block"
import { default as BlockWrapper } from "@/components/wrappers/block"
import styles from "./checkout-form-light.module.css"
import { Spinner } from "@/components/common/loading/spinner"
import { requestGiftCount } from "@/lib/api/gift"
import { formatNumber } from "@/lib/helpers/formatNumber"
import { default as ProgressBarElem } from "@/common/progress-bar/progress-bar"

export default function CheckoutFormLight(props) {
  const { amounts, heading, text, button, minAmount } = props.block.attrs

  let width = "bleed"
  let image = false
  if (props.settings?.image) {
    image = props.settings.image
  } else if (props.block?.attrs?.image) {
    image = props.block.attrs.image
  } else {
    width = "column"
  }

  let defaultValue = amounts.find((amount) => amount.isDefault)
  defaultValue = defaultValue || amounts[0]

  const [isCustomValue, setIsCustomValue] = useState(false)
  const [currentCount, setCurrentCount] = useState(false)

  const {
    setPaymentAmount,
    setHasUsedAmount,
    setSkipAmountStep,
    skipAmountStep,
    setCampaignName
  } = useAppContext()

  const methods = useForm({
    defaultValues: {
      amount: defaultValue.amount,
      customValue: minAmount
    },
    shouldUnregister: true
  })

  useEffect(() => {
    setHasUsedAmount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch count
  useEffect(() => {
    if (!fundraisingActive) {
      return
    }
    fetchCount(props.block.attrs.fundraisingCampaignId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.block.attrs.fundraisingCampaignId])

  const fetchCount = async (id) => {
    let data = await requestGiftCount(id)
    setCurrentCount(data.amount)
    setCampaignName(id)
  }

  const amount = methods.watch("amount")

  const onSubmit = (values) => {
    setPaymentAmount(isCustomValue ? values.customValue : values.amount, false)
    setSkipAmountStep(true)
  }

  const fundraisingActive = props.block?.attrs?.fundraisingActive
  const fundraisingGoal = props.block?.attrs?.fundraisingGoal
  const fundraisingDaysLeft = props.block?.attrs?.fundraisingDaysLeft

  return (
    <FormProvider {...methods}>
      <BlockWrapper>
        <div className={styles[width]}>
          {image && (
            <div className="col-span-1 mx-auto">
              <Image
                src={image.url}
                sizes={image.sizes}
                alt={image.alt}
                width={image.width}
                height={image.height}
                loading="lazy"
              />
            </div>
          )}
          <div className="col-span-1">
            <Block width={width} gap="none">
              <form
                onSubmit={methods.handleSubmit(onSubmit)}
                className="w-full">
                <div className="p-6 bg-white border border-green-100 rounded">
                  <div className="flex-col">
                    <span className="block pb-0 text-lg font-bold lg:text-3xl">
                      {heading}
                    </span>
                    <span className="block pb-0 mt-4 text-lg">{text}</span>
                  </div>
                  <div className="flex flex-wrap my-6 font-bold border divide-y rounded border-black-100/20 divide-black-50 border-black-50 lg:divide-y-0 lg:divide-x">
                    {amounts.map((amountObj) => (
                      <AmountItem
                        id="amountLight"
                        key={amountObj.amount}
                        amount={amountObj.amount}
                        isSelected={
                          amount == amountObj.amount && !isCustomValue
                        }
                        onClick={() => setIsCustomValue(false)}
                      />
                    ))}
                  </div>
                  <div className="mb-6">
                    {isCustomValue && (
                      <CustomValue id="Light" minAmount={minAmount} />
                    )}

                    <label
                      htmlFor="customValueLight"
                      className={`flex items-center px-4 py-4 text-lg font-bold border border-black-50 rounded cursor-pointer group ${
                        isCustomValue ? "hidden" : ""
                      }`}
                      onClick={() => setIsCustomValue(true)}>
                      <CheckmarkCircle />
                      Valfritt
                    </label>
                  </div>
                  <div className="flex justify-center">
                    <Button
                      theme="green"
                      className="flex-grow md:flex-grow-0 align-center"
                      type="submit"
                      ariaLabel={button ?? "Nästa"}
                      disabled={skipAmountStep}>
                      {button ?? "Nästa"}
                    </Button>
                  </div>
                </div>
              </form>
            </Block>
            {fundraisingActive && currentCount && (
              <div className="mt-10">
                <ProgressBarElem
                  count={currentCount}
                  total={fundraisingGoal}
                  text={"av " + formatNumber(fundraisingGoal) + " kr"}
                  units="kr"
                />
                <div className="-mt-4 text-center">
                  {fundraisingDaysLeft}
                  {fundraisingDaysLeft == 1 ? <> dag </> : <> dagar </>}
                  kvar
                </div>
              </div>
            )}

            {fundraisingActive && !currentCount && (
              <div className="flex items-center justify-center p-10 w-100 align-center">
                <Spinner />
              </div>
            )}
          </div>
        </div>
      </BlockWrapper>
    </FormProvider>
  )
}
